// A notification burst requests one follow-up snapshot, never concurrent writers.
export function createRefreshController({ load, commit, onError = () => {}, schedule = setTimeout, cancel = clearTimeout }) {
  let disposed = false;
  let generation = 0;
  let running = null;
  let dirty = false;
  let retryTimer = null;
  let failures = 0;
  let abort = null;

  function requestRefresh() {
    if (disposed) return Promise.resolve();
    if (running) {
      dirty = true;
      return running;
    }
    if (retryTimer !== null) cancel(retryTimer);
    retryTimer = null;
    dirty = false;
    const current = generation;
    running = Promise.resolve().then(async () => {
      if (disposed || current !== generation) return;
      try {
        const snapshot = await load(fn => { abort = fn; });
        if (disposed || current !== generation) return;
        commit(snapshot);
        failures = 0;
      } catch (error) {
        if (disposed || current !== generation) return;
        failures++;
        onError(error);
        // A failed burst gets one bounded retry, not an immediate retry per event.
        dirty = false;
        retryTimer = schedule(() => {
          retryTimer = null;
          requestRefresh();
        }, Math.min(5000 * Math.pow(2, Math.min(failures - 1, 4)), 60000));
      } finally {
        abort = null;
      }
    }).finally(() => {
      running = null;
      if (!disposed && dirty) requestRefresh();
    });
    return running;
  }

  function dispose() {
    disposed = true;
    generation++;
    dirty = false;
    if (retryTimer !== null) cancel(retryTimer);
    retryTimer = null;
    if (abort) abort();
  }
  return { requestRefresh, dispose };
}
